import { useRef, useEffect, useState, FC } from "react";
import type { ChartData, ScriptableContext } from "chart.js";
import { Chart as ChartJS, registerables } from "chart.js";
import { Bar } from "react-chartjs-2";
import { cn } from "src/util/cn";
import useWindowSize from "src/context/useWindowSize";
import Loading from "./Loading";
import ChartDataLabels from "chartjs-plugin-datalabels";

interface ChartBarProps {
  chartDataFromSource: { [key: string]: number | string };
  tooltipLabel: string;
  className?: string;
  customLabels?: string[];
  barThickness?: number;
  padding?: {
    left: number;
    right: number;
    top: number;
    bottom: number;
  };
}

ChartJS.register(...registerables, ChartDataLabels);

export const ChartBar: FC<ChartBarProps> = ({
  chartDataFromSource,
  tooltipLabel,
  className,
  customLabels,
  barThickness = 58,
  padding,
}) => {
  const chartRef = useRef<any>(null);
  const { isSm, isLg } = useWindowSize();
  const [data, setData] = useState<
    ChartData<"bar", (number | [number, number] | null)[], unknown>
  >({
    labels: [],
    datasets: [],
  });

  const getResponsiveLengthArr = (dataArr: any[]) => {
    let sliceCount =
      // !isLg ? 20 :
      0;
    return dataArr.length > sliceCount ? dataArr.slice(-sliceCount) : dataArr;
  };

  const parseDates = (labels: string[]) => {
    return labels.map((dateStr) => new Date(dateStr));
  };

  const isZeroToThirtyDayRange = (startDate: Date, endDate: Date) => {
    const diffTime = endDate?.getTime() - startDate?.getTime();
    return (
      diffTime >= 0 && 
      diffTime <= 30 * 24 * 60 * 60 * 1000 
    );
  };
  const isOneToTwoMonthRange = (startDate: Date, endDate: Date) => {
    const diffTime = endDate?.getTime() - startDate?.getTime();
    return (
      diffTime >= 30 * 24 * 60 * 60 * 1000 &&
      diffTime <= 60 * 24 * 60 * 60 * 1000
    );
  };
  const isTwoToFiveMonthRange = (startDate: Date, endDate: Date) => {
    const diffTime = endDate?.getTime() - startDate?.getTime();
    return (
      diffTime > 60 * 24 * 60 * 60 * 1000 &&  
      diffTime <= 150 * 24 * 60 * 60 * 1000 
    );
  };
  
  const isTwoToSixMonthRange = (startDate: Date, endDate: Date) => {
    const diffTime = endDate?.getTime() - startDate?.getTime();
    return (
      diffTime > 60 * 24 * 60 * 60 * 1000 &&
      diffTime <= 180 * 24 * 60 * 60 * 1000
    );
  };

  const isFiveMonthToOneYearRange = (startDate: Date, endDate: Date) => {
    const diffTime = endDate?.getTime() - startDate?.getTime();
    const fiveMonthsInMs = 150 * 24 * 60 * 60 * 1000; 
    const oneYearInMs = 365 * 24 * 60 * 60 * 1000;     
    
    return diffTime > fiveMonthsInMs && diffTime <= oneYearInMs;
  };
  
  const isSixMonthToOneYearRange = (startDate: Date, endDate: Date) => {
    const diffTime = endDate?.getTime() - startDate?.getTime();
    const sixMonthsInMs = 180 * 24 * 60 * 60 * 1000;
    const oneYearInMs = 365 * 24 * 60 * 60 * 1000;
    return diffTime > sixMonthsInMs && diffTime <= oneYearInMs;
  };
  const isOneYearToTwoYearsRange = (startDate: Date, endDate: Date) => {
    const diffTime = endDate?.getTime() - startDate?.getTime();
    const oneYearInMs = 365 * 24 * 60 * 60 * 1000;    
    const twoYearsInMs = 730 * 24 * 60 * 60 * 1000;   
    
    return diffTime > oneYearInMs && diffTime <= twoYearsInMs;
  };
  const isTwoYearsAndAboveRange = (startDate: Date, endDate: Date) => {
    const diffTime = endDate?.getTime() - startDate?.getTime();
    const twoYearsInMs = 730 * 24 * 60 * 60 * 1000;   
    
    return diffTime >= twoYearsInMs;  
  };
  
  const isOverOneYearRange = (startDate: Date, endDate: Date) => {
    const diffTime = endDate?.getTime() - startDate?.getTime();
    return diffTime > 365 * 24 * 60 * 60 * 1000;
  };
  const generateThreeDayLabels = (labels: Date[],values: number[]) => {
    const threeDayWindow = 3;
    const startDate = labels[0];
    const endDate = labels[labels.length - 1];
    const newLabels: string[] = [];
    const newLabelsWithYear: string[] = [];
    const newData: number[] = [];
    let currentDate = new Date(startDate);
    const getMonthName = (date: Date) => {
      return date.toLocaleString("default", { month: "short" });
    };
      while (currentDate <= endDate) {
      const labelStart = `${getMonthName(currentDate)} ${currentDate.getDate()}`;
      const labelEnd = new Date(currentDate);
      labelEnd.setDate(labelEnd.getDate() + (threeDayWindow - 1));
  
      let formattedLabel;
      if (
        currentDate.getMonth() !== labelEnd.getMonth() ||
        currentDate.getFullYear() !== labelEnd.getFullYear()
      ) {
        const monthStart = getMonthName(currentDate);
        const monthEnd = getMonthName(labelEnd);
        formattedLabel = `${monthStart} ${currentDate.getDate()} - ${monthEnd} ${labelEnd.getDate()}`;
      } else {
        formattedLabel = `${labelStart} - ${labelEnd.getDate()}`;
      }
      newLabels.push(formattedLabel);
      newLabelsWithYear.push(`${formattedLabel}, ${labelEnd.getFullYear()}`);
  
      const sum = values.reduce((acc, value, index) => {
        const valueDate = new Date(labels[index]);
        return valueDate >= currentDate && valueDate <= labelEnd
          ? acc + (value || 0)
          : acc;
      }, 0);
      newData.push(sum);
  
      currentDate.setDate(currentDate.getDate() + threeDayWindow);
    }
      while (
      newData.length <
      Math.ceil(
        (endDate.getTime() - startDate.getTime()) /
          (threeDayWindow * 24 * 60 * 60 * 1000)
      )
    ) {
      const nextLabelDate = new Date(currentDate);
      const labelStart = `${getMonthName(nextLabelDate)} ${nextLabelDate.getDate()}`;
      const labelEnd = new Date(nextLabelDate);
      labelEnd.setDate(labelEnd.getDate() + (threeDayWindow - 1));
  
      let formattedLabel;
      if (
        nextLabelDate.getMonth() !== labelEnd.getMonth() ||
        nextLabelDate.getFullYear() !== labelEnd.getFullYear()
      ) {
        const monthStart = getMonthName(nextLabelDate);
        const monthEnd = getMonthName(labelEnd);
        formattedLabel = `${monthStart} ${nextLabelDate.getDate()} - ${monthEnd} ${labelEnd.getDate()}`;
      } else {
        formattedLabel = `${labelStart} - ${labelEnd.getDate()}`;
      }
  
      newLabels.push(formattedLabel);
      newLabelsWithYear.push(`${formattedLabel}, ${labelEnd.getFullYear()}`);
      newData.push(0);
  
      currentDate.setDate(currentDate.getDate() + threeDayWindow);
    }
  
    return { newLabels, newLabelsWithYear, newData };
  };
  
  const generateWeeklyLabelsAndData = (labels: Date[], values: number[]) => {
    const startDate = labels[0];
    const endDate = labels[labels.length - 1];
    const newLabels: string[] = [];
    const newLabelsWithYear: string[] = [];
    const newData: number[] = [];
    let currentDate = new Date(startDate);
  
    while (currentDate <= endDate) {
      const labelEnd = new Date(currentDate);
      labelEnd.setDate(labelEnd.getDate() + 6);
      const startMonth = currentDate.toLocaleString("default", { month: "short" });
      const startDay = currentDate.getDate();
      const endMonth = labelEnd.toLocaleString("default", { month: "short" });
      const endDay = labelEnd.getDate();
      const year = labelEnd.getFullYear();
  
      let formattedLabel;
      if (
        currentDate.getMonth() !== labelEnd.getMonth() ||
        currentDate.getFullYear() !== labelEnd.getFullYear()
      ) {
        formattedLabel = `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
      } else {
        formattedLabel = `${startMonth} ${startDay} - ${endDay}`;
      }
  
      newLabels.push(formattedLabel);
      newLabelsWithYear.push(`${formattedLabel}, ${year}`);
  
      const sum = values.reduce((acc, value, index) => {
        const valueDate = new Date(labels[index]);
        return valueDate >= currentDate && valueDate <= labelEnd
          ? acc + (value || 0)
          : acc;
      }, 0);
      newData.push(sum);
      currentDate.setDate(currentDate.getDate() + 7);
    }
  
    return { newLabels, newLabelsWithYear, newData };
  };
  

  const generateTwoWeeklyLabelsAndData = (labels: Date[], values: number[]) => {
    const startDate = labels[0];
    const endDate = labels[labels.length - 1];
    const newLabels: string[] = [];
    const newLabelsWithYear: string[] = [];
    const newData: number[] = [];
    let currentDate = new Date(startDate);
  
    while (currentDate <= endDate) {
      const labelEnd = new Date(currentDate);
      labelEnd.setDate(labelEnd.getDate() + 13);
  
      const startMonth = currentDate.toLocaleString("default", { month: "short" });
      const startDay = currentDate.getDate();
      const endMonth = labelEnd.toLocaleString("default", { month: "short" });
      const endDay = labelEnd.getDate();
      const year = labelEnd.getFullYear();
  
      let formattedLabel;
      if (
        currentDate.getMonth() !== labelEnd.getMonth() ||
        currentDate.getFullYear() !== labelEnd.getFullYear()
      ) {
        formattedLabel = `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
      } else {
        formattedLabel = `${startMonth} ${startDay} - ${endDay}`;
      }
  
      newLabels.push(formattedLabel);
      newLabelsWithYear.push(`${formattedLabel}, ${year}`);
  
      const sum = values.reduce((acc, value, index) => {
        const valueDate = new Date(labels[index]);
        return valueDate >= currentDate && valueDate <= labelEnd
          ? acc + (value || 0)
          : acc;
      }, 0);
      newData.push(sum);
  
      currentDate.setDate(currentDate.getDate() + 14);
    }  
    return { newLabels, newLabelsWithYear, newData };
  };
  

  const generateMonthlyLabelsAndData = (labels: Date[], values: number[]) => {
    const startDate = labels[0];
    const endDate = labels[labels.length - 1];
    const newLabels: string[] = [];
    const newData: number[] = [];
    const newLabelsWithYear: string[] = [];
    let currentDate = new Date(startDate);
  
    while (currentDate <= endDate) {
      const labelStartMonth = currentDate.toLocaleString("default", { month: "short" });
      const labelStartDay = currentDate.getDate();
      const labelEnd = new Date(currentDate);
      labelEnd.setMonth(labelEnd.getMonth() + 1);
      labelEnd.setDate(labelEnd.getDate() - 1); 
  
      const labelEndMonth = labelEnd.toLocaleString("default", { month: "short" });
      const labelEndDay = labelEnd.getDate();
      const year = labelEnd.getFullYear();
  
      let formattedLabel;
      if (
        currentDate.getMonth() !== labelEnd.getMonth() ||
        currentDate.getFullYear() !== labelEnd.getFullYear()
      ) {
        formattedLabel = `${labelStartMonth} ${labelStartDay} - ${labelEndMonth} ${labelEndDay}`;
      } else {
        formattedLabel = `${labelStartMonth} ${labelStartDay} - ${labelEndDay}`;
      }
      
      const formattedLabelWithYear = `${formattedLabel}, ${year}`;
      newLabels.push(formattedLabel);
      newLabelsWithYear.push(formattedLabelWithYear);
  
      const sum = values.reduce((acc, value, index) => {
        const valueDate = new Date(labels[index]);
        return valueDate >= currentDate && valueDate <= labelEnd
          ? acc + (value || 0)
          : acc;
      }, 0);
      newData.push(sum);
  
      currentDate.setMonth(currentDate.getMonth() + 1);
    }
  
    return { newLabels, newData, newLabelsWithYear };
  };
  
  const generateQuarterlyLabelsAndData = (labels: Date[], values: number[]) => {
    const startDate = labels[0];
    const endDate = labels[labels.length - 1];
    const newLabels: string[] = [];
    const newData: number[] = [];
    const newLabelsWithYear: string[] = [];
    let currentDate = new Date(startDate);
  
    const getMonthName = (date: Date) => {
      return date.toLocaleString("default", { month: "short" });
    };
  
    while (currentDate <= endDate) {
      const labelStartMonth = getMonthName(currentDate);
      const labelStartDay = currentDate.getDate();
  
      const labelEnd = new Date(currentDate);
      labelEnd.setMonth(labelEnd.getMonth() + 3);
      labelEnd.setDate(labelEnd.getDate() - 1); 
  
      const labelEndMonth = getMonthName(labelEnd);
      const labelEndDay = labelEnd.getDate();
      const year = labelEnd.getFullYear();
  
      let formattedLabel;
      if (
        currentDate.getMonth() !== labelEnd.getMonth() ||
        currentDate.getFullYear() !== labelEnd.getFullYear()
      ) {
        formattedLabel = `${labelStartMonth} ${labelStartDay} - ${labelEndMonth} ${labelEndDay}`;
      } else {
        formattedLabel = `${labelStartMonth} ${labelStartDay} - ${labelEndDay}`;
      }
  
      const formattedLabelWithYear = `${formattedLabel}, ${year}`;
      newLabels.push(formattedLabel);
      newLabelsWithYear.push(formattedLabelWithYear);
  
      const sum = values.reduce((acc, value, index) => {
        const valueDate = new Date(labels[index]);
        return valueDate >= currentDate && valueDate <= labelEnd
          ? acc + (value || 0)
          : acc;
      }, 0);
      newData.push(sum);
  
      currentDate.setMonth(currentDate.getMonth() + 3);
    }
  
    return { newLabels, newData, newLabelsWithYear };
  };
  const generateYearlyLabelsAndData = (labels: Date[], values: number[]) => {
    const startDate = labels[0];
    const endDate = labels[labels.length - 1];
    const newLabels: string[] = [];
    const newData: number[] = [];
    const newLabelsWithYear: string[] = [];
    let currentDate = new Date(startDate);
  
    const getYear = (date: Date) => {
      return date.getFullYear();
    };
  
    while (currentDate <= endDate) {
      const year = getYear(currentDate);
      
      const labelStart = new Date(year, 0, 1); 
      const labelEnd = new Date(year + 1, 0, 0); 
      
      const formattedLabel = `${year}`;
      const formattedLabelWithYear = `${formattedLabel}, ${year}`;
      
      newLabels.push(formattedLabel);
      newLabelsWithYear.push(formattedLabelWithYear);
  
      const sum = values.reduce((acc, value, index) => {
        const valueDate = new Date(labels[index]);
        return valueDate >= labelStart && valueDate <= labelEnd
          ? acc + (value || 0)
          : acc;
      }, 0);
      newData.push(sum);
  
      currentDate.setFullYear(currentDate.getFullYear() + 1);
    }
  
    return { newLabels, newData, newLabelsWithYear };
  };
  
  const generateLabelsAndData = (
    labels: Date[],
    values: number[]
  ): {
    newLabels: string[];
    newData: number[];
    newLabelsWithYear?: string[];
  } => {
    const startDate = labels[0];
    const endDate = labels[labels.length - 1];
    const newLabels: string[] = [];
    const newData: number[] = [];
    const newLabelsWithYear: string[] = [];

    if(!isLg){
      if(isZeroToThirtyDayRange(startDate, endDate)){
        return generateThreeDayLabels(labels, values)
      }else if(isOneToTwoMonthRange(startDate, endDate)){
        return generateWeeklyLabelsAndData(labels, values)
      }else if(isTwoToFiveMonthRange(startDate, endDate)){
        return generateTwoWeeklyLabelsAndData(labels, values)
      }else if(isFiveMonthToOneYearRange(startDate, endDate)){
        return generateMonthlyLabelsAndData(labels, values)
      }else if(isOneYearToTwoYearsRange(startDate, endDate)){
        return generateQuarterlyLabelsAndData(labels, values)
      }else if(isTwoYearsAndAboveRange(startDate,endDate)){
        generateYearlyLabelsAndData(labels, values)
      } else{
        labels.forEach((label, index) => {
          const date = new Date(label);
          const month = date.toLocaleString("default", { month: "short" });
          const day = date.getDate();
          const formattedLabel = `${month} ${day}`;
          newLabels.push(formattedLabel);
          newLabelsWithYear.push(`${formattedLabel}, ${date.getFullYear()}`);
          newData.push(values[index] || 0);
        });
      }

    }else {

      if (isOneToTwoMonthRange(startDate, endDate)) {
        return generateThreeDayLabels(labels, values)
      } else if (isTwoToSixMonthRange(startDate, endDate)) {
        return generateWeeklyLabelsAndData(labels, values);
      } else if (isSixMonthToOneYearRange(startDate, endDate)) {
        return generateTwoWeeklyLabelsAndData(labels, values);
      } else if (isOverOneYearRange(startDate, endDate)) {
        return generateMonthlyLabelsAndData(labels, values);
      } else {
        labels.forEach((label, index) => {
          const date = new Date(label);
          const month = date.toLocaleString("default", { month: "short" });
          const day = date.getDate();
          const formattedLabel = `${month} ${day}`;
          newLabels.push(formattedLabel);
          newLabelsWithYear.push(`${formattedLabel}, ${date.getFullYear()}`);
          newData.push(values[index] || 0);
        });
      }
    }
      
    return { newLabels, newData, newLabelsWithYear };
  };

  const chartLabels = getResponsiveLengthArr(
    customLabels || Object.keys(chartDataFromSource)
  );
  const chartDataValues = getResponsiveLengthArr(
    Object.values(chartDataFromSource).map(Number)
  );
  const parsedLabels: any = parseDates(chartLabels);

  const { newLabels, newData, newLabelsWithYear } = generateLabelsAndData(
    parsedLabels,
    chartDataValues
  );
  const calculateMaxValue = (data: number[]) => {
    const maxDataValue = Math.max(...data);
    if (maxDataValue <= 0) return 1;
    return Math.ceil(maxDataValue * 1.2);
  };
  const options = {
    maintainAspectRatio: false,
    interaction: { intersect: false, mode: "index" },
    layout: {
      padding: padding || {
        left: !isSm ? 10 : 50,
        right: 0,
        top: 0,
        bottom: 0,
      },
    },
    scales: {
      y: {
        position: "right",
        gridLines: { color: "rgba(0, 0, 0, 0)" },
        max: calculateMaxValue(newData),
        grid: {
          display: false,
          drawOnChartArea: false,
          drawTicks: false,
        },
        ticks: {
          font: { size: 12 },
          callback: (value: number, index: number, values: any[]) => {
            const totalValues = values?.length;
            const formatNumber = (num: number = 0) => {
              return num >= 10000 ? num.toLocaleString() : num.toString();
            };
            if (index === totalValues - 1) {
            return ''
            }
            if (value === 0) {
              return "0";
            } else {
              return `${formatNumber(value)}`;
            }
          },
        },
      },
      x: {
        gridLines: { color: "rgba(0, 0, 0, 0)" },
        grid: {
          display: false,
          drawOnChartArea: false,
          drawTicks: false,
        },
        ticks: {
          font: { size: 12 },
          padding: 10,
        },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        padding: 12,
        caretPadding: 18,
        caretSize: 0,
        displayColors: false,
        titleFont: { size: 10 },
        bodyFont: { size: 12 },
        titleColor: "hsla(0, 0%, 63%, 1)",
        callbacks: {
          title: (tooltipItem: { label: string; dataIndex: number }[]) => {
            return newLabelsWithYear
              ? `${newLabelsWithYear?.[tooltipItem?.[0]?.dataIndex]}`
              : `${tooltipItem[0].label}`;
          },
          label: (tooltipItem: any) => {
            const value = tooltipItem?.raw;
            const formatNumber = (num: number = 0) => {
              return num >= 1000 ? num.toLocaleString() : num.toString();
            };
            return `${tooltipLabel}: ${formatNumber(value) || 0}`;
          },
          labelTextColor: () => "hsla(0, 0%, 44%, 1)",
        },
      },
      datalabels: {
        anchor: "end",
        align: "end",
        formatter: (value: number) => {
          const formatNumber = (num: number = 0) => {
            return num >= 1000 ? num.toLocaleString() : num.toString();
          };
          return formatNumber(value);
        },
        color: "#FFFFFF66",
        font: {
          weight: 500,
        },
      },
    },
  }as const;
  

  useEffect(() => {
    setData({
      labels: newLabels,
      datasets: [
        {
          label: tooltipLabel,
          data: newData,
          borderRadius: 10,
          backgroundColor: (context: ScriptableContext<"bar">) => {
            const ctx = context.chart.ctx;
            const value = context.raw as number;
            const maxValue = context.chart.scales['y'].max; 
            if (isNaN(value) || value === null || value === undefined) {
              return "rgba(0, 0, 0, 0)"; 
            }
            const gradient = ctx.createLinearGradient(0, 0, 0, 315);
            const valuePercentage = value / maxValue;
          
            if (valuePercentage > 0.5) {
              gradient.addColorStop(0.2, "rgba(95, 67, 255, 1)"); 
              gradient.addColorStop(0.99, "rgba(86, 39, 191, 0.01)"); 
              gradient.addColorStop(1, "transparent"); 
            } else {
              gradient.addColorStop(0, "rgba(95, 67, 255, 1)"); 
              gradient.addColorStop(valuePercentage * 0.8, "rgba(95, 67, 255, 0.6)"); 
              gradient.addColorStop(0.8, "rgba(86, 39, 191, 0.6)"); 
            }
          
            return gradient;
          },
          hoverBackgroundColor: (context: ScriptableContext<"bar">) => {
            const value = context.raw as number; 
            const maxValue = Math.max(...newData); 
            const ratio = value / maxValue; 
            const valuePercentage = ratio * 100; 
            const ctx = context.chart.ctx;
            const gradient = ctx.createLinearGradient(0, 0, 0, 315);
          
            if (valuePercentage > 50) {
              gradient.addColorStop(0.5, `rgba(95, 67, 255, ${Math.min(ratio + 0.1, 1)})`);
              gradient.addColorStop(1, `rgba(86, 39, 191, ${Math.min(ratio * 0.1 + 0.05, 0.9)})`);
            } 
            else {
              gradient.addColorStop(0, "rgba(95, 67, 255, 1)"); 
              gradient.addColorStop(valuePercentage * 0.8 / 100, "rgba(95, 67, 255, 0.6)"); 
              gradient.addColorStop(1, "rgba(86, 39, 191, 0.9)"); 
            }
          
            return gradient;
          },
          barThickness: !isSm
            ? Math.max(15, 23 / Math.ceil(newLabels.length / 7))
            : Math.max(28, barThickness / Math.ceil(newLabels.length / 7)),
        },
      ],
    });
  }, [isSm, isLg, chartDataFromSource]);

  return (
    <div className={cn("h-[350px] overflow-hidden relative", className)}>
      <Bar ref={chartRef} data={data} options={options} />
      {!chartRef.current && <Loading />}
    </div>
  );
};
